import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

/**
 * Modal for selecting multiple option_groups on a menu item.
 *
 * Props:
 *  - item: menu item with option_groups (or legacy options)
 *  - language: 'es' | 'en' | 'he'
 *  - currency: 'MXN' | 'USD'
 *  - usdRate: number (fallback conversion if extra_price_usd missing)
 *  - onClose(): close without adding
 *  - onConfirm(selectedOptions): array of {group_id,label_es,label_en,label_he,extra_price,extra_price_usd}
 */
const OptionGroupsModal = ({ item, language, currency, usdRate, onClose, onConfirm }) => {
  // Normalize legacy `options` field into a single anonymous group when option_groups is missing
  const groups = useMemo(() => {
    if (item.option_groups && item.option_groups.length > 0) return item.option_groups;
    if (item.options && item.options.length > 0) {
      return [{
        id: 'option',
        label_es: 'Opcion', label_en: 'Option', label_he: 'אפשרות',
        type: 'single', required: true,
        options: item.options,
      }];
    }
    return [];
  }, [item]);

  const [selections, setSelections] = useState({}); // groupId -> opt | [opt,...]
  const [errorGroupId, setErrorGroupId] = useState(null);

  const labelOf = (obj, key) => obj[`${key}_${language}`] || obj[`${key}_es`] || obj[key] || '';
  const itemName = labelOf(item, 'name');

  const toggleSingle = (gid, opt) => {
    setSelections(prev => ({ ...prev, [gid]: opt }));
    setErrorGroupId(null);
  };

  const toggleMulti = (gid, opt) => {
    setSelections(prev => {
      const list = prev[gid] || [];
      const key = opt.label_es;
      const exists = list.find(o => o.label_es === key);
      const next = exists ? list.filter(o => o.label_es !== key) : [...list, opt];
      return { ...prev, [gid]: next };
    });
  };

  const formatExtra = (opt) => {
    const mxn = opt.extra_price || 0;
    if (mxn <= 0) return '';
    if (currency === 'USD') {
      const usd = opt.extra_price_usd != null ? opt.extra_price_usd : mxn * usdRate;
      return `+$${usd.toFixed(usd < 1 ? 2 : 2)} USD`;
    }
    return `+$${mxn} MXN`;
  };

  const isSelected = (gid, opt, type) => {
    const sel = selections[gid];
    if (!sel) return false;
    if (type === 'single') return sel.label_es === opt.label_es;
    return Array.isArray(sel) && sel.some(o => o.label_es === opt.label_es);
  };

  const handleConfirm = () => {
    // Validate required groups
    for (const g of groups) {
      if (g.required) {
        const sel = selections[g.id];
        const ok = g.type === 'single' ? !!sel : Array.isArray(sel) && sel.length > 0;
        if (!ok) {
          setErrorGroupId(g.id);
          toast.error(language === 'es' ? `Selecciona: ${labelOf(g, 'label')}` :
                      language === 'en' ? `Choose: ${labelOf(g, 'label')}` :
                                          `בחר: ${labelOf(g, 'label')}`);
          return;
        }
      }
    }
    // Build flat selected_options list
    const out = [];
    for (const g of groups) {
      const sel = selections[g.id];
      if (!sel) continue;
      const arr = Array.isArray(sel) ? sel : [sel];
      for (const o of arr) {
        out.push({
          group_id: g.id,
          label_es: o.label_es,
          label_en: o.label_en,
          label_he: o.label_he,
          extra_price: Number(o.extra_price || 0),
          extra_price_usd: Number(o.extra_price_usd || 0),
        });
      }
    }
    onConfirm(out);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="option-groups-modal"
    >
      <Card className="w-full max-w-md bg-white border-[#E5E3D8] rounded-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="sticky top-0 bg-white z-10 border-b border-[#E5E3D8]">
          <CardTitle className="font-heading text-[#0F2922]" data-testid="option-modal-item-name">{itemName}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 pt-5">
          {groups.length === 0 && (
            <p className="text-sm text-[#5C6C62]">
              {language === 'es' ? 'Sin opciones disponibles.' : language === 'en' ? 'No options available.' : 'אין אפשרויות.'}
            </p>
          )}
          {groups.map((g) => {
            const isMulti = g.type === 'multi';
            const groupLabel = labelOf(g, 'label');
            const hasError = errorGroupId === g.id;
            return (
              <div key={g.id} data-testid={`option-group-${g.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading font-semibold text-[#0F2922]">
                    {groupLabel}
                    {g.required && <span className="text-[#B85042] ml-1">*</span>}
                  </h4>
                  <span className="text-xs text-[#5C6C62] uppercase tracking-wider">
                    {isMulti
                      ? (language === 'es' ? 'Multiple' : language === 'en' ? 'Multiple' : 'מרובה')
                      : (language === 'es' ? 'Una' : language === 'en' ? 'One' : 'אחת')}
                  </span>
                </div>
                <div className={`grid grid-cols-1 gap-2 ${hasError ? 'ring-2 ring-[#B85042] rounded-xl p-2' : ''}`}>
                  {g.options.map((opt, idx) => {
                    const selected = isSelected(g.id, opt, g.type);
                    const optLabel = labelOf(opt, 'label');
                    return (
                      <button
                        key={`${g.id}-${idx}`}
                        type="button"
                        onClick={() => isMulti ? toggleMulti(g.id, opt) : toggleSingle(g.id, opt)}
                        className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between border ${
                          selected
                            ? 'bg-[#0F2922] text-white border-[#0F2922]'
                            : 'bg-[#FAF9F6] text-[#0F2922] border-transparent hover:border-[#B85042]/30'
                        }`}
                        data-testid={`opt-${g.id}-${idx}`}
                      >
                        <span className="font-body font-medium flex items-center gap-2">
                          {selected && <Check className="w-4 h-4" />}
                          {optLabel}
                        </span>
                        {opt.extra_price > 0 && (
                          <span className={`text-sm ${selected ? 'text-white/90' : 'text-[#B85042]'}`}>
                            {formatExtra(opt)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
        <div className="sticky bottom-0 bg-white border-t border-[#E5E3D8] p-4 flex gap-3">
          <Button
            variant="ghost"
            className="flex-1 text-[#5C6C62]"
            onClick={onClose}
            data-testid="option-modal-cancel"
          >
            {language === 'es' ? 'Cancelar' : language === 'en' ? 'Cancel' : 'בטל'}
          </Button>
          <Button
            className="flex-1 bg-[#0F2922] hover:bg-[#0F2922]/90 text-white rounded-xl"
            onClick={handleConfirm}
            data-testid="option-modal-confirm"
          >
            {language === 'es' ? 'Anadir al carrito' : language === 'en' ? 'Add to cart' : 'הוסף לעגלה'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OptionGroupsModal;
